import React from 'react';
import { SvgIconProps } from '@mui/material/SvgIcon';
import ImageIcon from '@mui/icons-material/Image';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import CropOriginalIcon from '@mui/icons-material/CropOriginal';
import BrushIcon from '@mui/icons-material/Brush';
import axiosInstance from '../services/axiosInstance';
import { IAsset } from '../types/damTypes';

export type BuiltInDownloadTemplateId =
  | 'original'
  | 'web'
  | 'transparent'
  | 'illustration';

export interface BuiltInDownloadTemplate {
  id: BuiltInDownloadTemplateId;
  name: string;
  tooltip: string;
  endpoint: string;
  icon: React.ReactElement<SvgIconProps>;
}

export interface ActiveDownloadTemplate {
  id: string;
  name: string;
}

export const BUILT_IN_DOWNLOAD_TEMPLATES: BuiltInDownloadTemplate[] = [
  {
    id: 'original',
    name: 'Originalbild',
    tooltip: 'Originalfilen i full storlek, utan bearbetning.',
    endpoint: '/download',
    icon: <ImageIcon fontSize="small" />,
  },
  {
    id: 'web',
    name: 'Webbild',
    tooltip: 'Anpassad för webben. Bildens längsta sida blir högst 1920 px.',
    endpoint: '/download/web',
    icon: <DesktopWindowsIcon fontSize="small" />,
  },
  {
    id: 'transparent',
    name: 'Transparent PNG',
    tooltip: 'Sparar bilden som PNG med transparent bakgrund. Bra för produktbilder och vinbilder.',
    endpoint: '/download/transparent',
    icon: <CropOriginalIcon fontSize="small" />,
  },
  {
    id: 'illustration',
    name: 'Illustration',
    tooltip: 'Konverterar vektorfiler till PNG med transparent bakgrund.',
    endpoint: '/download/illustration',
    icon: <BrushIcon fontSize="small" />,
  },
];

export const fetchActiveDownloadTemplates = async (): Promise<
  ActiveDownloadTemplate[]
> => {
  const { data } = await axiosInstance.get('/api/download-templates/active');
  return Array.isArray(data) ? data : [];
};

export const getAvailableBuiltInDownloadTemplates = (
  asset: IAsset
): BuiltInDownloadTemplate[] => {
  const fileType = asset.fileType || '';
  const fileName = (asset.fileName || '').toLowerCase();
  const isImageFile = fileType.startsWith('image/');
  const isVectorFile =
    fileType.includes('ai') ||
    fileType.includes('eps') ||
    fileType.includes('svg') ||
    fileType.includes('pdf') ||
    fileType === 'application/postscript' ||
    fileType === 'application/illustrator' ||
    fileName.endsWith('.ai') ||
    fileName.endsWith('.eps') ||
    fileName.endsWith('.pdf');

  return BUILT_IN_DOWNLOAD_TEMPLATES.filter((template) => {
    if (template.id === 'original') return true;
    if (template.id === 'web' && isImageFile) return true;
    if (template.id === 'transparent' && isImageFile) return true;
    if (template.id === 'illustration' && isVectorFile) return true;
    return false;
  });
};

const getBuiltInDownloadTemplate = (
  templateId: BuiltInDownloadTemplateId
): BuiltInDownloadTemplate => {
  const template = BUILT_IN_DOWNLOAD_TEMPLATES.find((item) => item.id === templateId);
  if (!template) {
    throw new Error('Nedladdningsformat saknas.');
  }

  return template;
};

const getDownloadFileName = (
  asset: IAsset,
  template: BuiltInDownloadTemplate
): string => {
  let downloadFileName = asset.fileName || 'download';

  if (template.id === 'web') {
    downloadFileName = downloadFileName.replace(/\.[^.]+$/, '_web$&');
  } else if (template.id === 'transparent') {
    downloadFileName = downloadFileName.replace(/\.[^.]+$/, '_transparent.png');
  } else if (template.id === 'illustration') {
    downloadFileName = downloadFileName.replace(/\.[^.]+$/, '_illustration.png');
  }

  return downloadFileName;
};

const triggerBrowserDownload = (blob: Blob, fileName: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const downloadAssetsWithBuiltInTemplate = async (
  assets: IAsset[],
  templateId: BuiltInDownloadTemplateId
) => {
  const template = getBuiltInDownloadTemplate(templateId);

  for (let i = 0; i < assets.length; i += 1) {
    const asset = assets[i];
    const response = await axiosInstance.get(`/api/assets/${asset._id}${template.endpoint}`, {
      responseType: 'blob',
    });

    const blob = new Blob([response.data], {
      type: response.headers['content-type'],
    });

    triggerBrowserDownload(blob, getDownloadFileName(asset, template));

    // Small delay helps avoid browser blocking multiple sequential downloads.
    if (i < assets.length - 1) {
      await new Promise((resolve) => setTimeout(resolve, 300));
    }
  }
};

export const runCustomAssetDownloadTemplate = async ({
  templateId,
  assetIds,
  primaryAssetFileName,
}: {
  templateId: string;
  assetIds: string[];
  primaryAssetFileName?: string;
}) => {
  const response = await axiosInstance.post(
    '/api/download-templates/render/assets',
    { templateId, assetIds },
    { responseType: 'arraybuffer' }
  );

  const contentType = response.headers['content-type'] || 'application/octet-stream';
  const disposition: string = response.headers['content-disposition'] || '';
  const match = disposition.match(/filename="?([^";]+)"?/i);
  const defaultName =
    assetIds.length === 1 ? primaryAssetFileName || 'download' : 'download.zip';
  const fileName = match
    ? decodeURIComponent(match[1])
    : contentType.includes('zip')
      ? 'download.zip'
      : defaultName;

  const blob = new Blob([response.data], { type: contentType });
  triggerBrowserDownload(blob, fileName);
};
